import { ASTRA_CHAT_TEST_IDS } from '@amzn/gravion-ui-contracts';
import React from 'react';
import type { CSSObject } from '@emotion/react';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import { AIButton, AIButtonVariant } from '@amzn/stencil-react-components/ai';
import { View } from '@amzn/stencil-react-components/layout';
import { IconSparklesFillMedium } from '@amzn/stencil-react-icons';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { useAstra } from '../features/astra';

/** Test id on the launcher button. */
export const ASTRA_DESKTOP_TRIGGER_TEST_ID = ASTRA_CHAT_TEST_IDS.desktopTrigger;

interface AstraDesktopTriggerProps {
  readonly isAuthenticated: boolean;
}

/**
 * Bottom-right anchor for the launcher, just inside the corner the panel
 * opens from. Sits below the panel's own layer so the two never overlap
 * during the open/close hand-off.
 */
const TRIGGER_STYLE: CSSObject = {
  position: 'fixed',
  bottom: token('dimensions.spacing.700'),
  right: token('dimensions.spacing.500'),
  zIndex: token('dimensions.z-index.800'),
};

/**
 * Floating launcher for the Stencil Astra panel on desktop: Stencil's primary
 * `AIButton` (the AI gradient surface) carrying the sparkles glyph, the same
 * mark the panel header and the mobile nav use.
 *
 * Only `StencilProvider` is mounted here (not the full `V2Adapter`): a button
 * needs tokens, not auth or i18n.
 */
export function AstraDesktopTrigger({
  isAuthenticated,
}: AstraDesktopTriggerProps): React.JSX.Element | null {
  const { state, togglePanel } = useAstra();
  if (!isAuthenticated || state.isOpen) return null;
  return (
    <StencilProvider themeMode={ThemeMode.Light}>
      <View css={TRIGGER_STYLE}>
        <AIButton
          variant={AIButtonVariant.Primary}
          icon={<IconSparklesFillMedium aria-hidden />}
          aria-label="Open Astra"
          onClick={togglePanel}
          dataTestId={ASTRA_DESKTOP_TRIGGER_TEST_ID}
        />
      </View>
    </StencilProvider>
  );
}
